<template>
  <div class="page-container">
    <header class="header">
      <el-button text class="back-btn" @click="handleBack" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="icon-back" />
      </el-button>
      <h1 class="title">备料单</h1>
      <div class="header-right"></div>
    </header>

    <main class="content" v-loading="loading" element-loading-text="加载中...">
      <div v-if="headerInfo" class="header-info">
        <div class="info-row">
          <div class="info-label">供应商</div>
          <div class="info-value">{{ headerInfo.supplierName || "-" }}</div>
        </div>
        <div class="info-row">
          <div class="info-label">仓库</div>
          <div class="info-value">{{ headerInfo.storeName || "-" }}</div>
        </div>
        <div class="info-row">
          <div class="info-label">主机厂</div>
          <div class="info-value">{{ headerInfo.factoryName || "-" }}</div>
        </div>
        <div class="info-row">
          <div class="info-label">子库</div>
          <div class="info-value">{{ headerInfo.subWareName || "-" }}</div>
        </div>
        <div class="info-row">
          <div class="info-label">需求日期</div>
          <div class="info-value">{{ headerInfo.demandTime || "-" }}</div>
        </div>
        <div class="info-row" v-if="headerInfo.prepareTime">
          <div class="info-label">备料时间</div>
          <div class="info-value">{{ headerInfo.prepareTime || "-" }}</div>
        </div>
      </div>

      <div v-if="statusMsg" class="status-banner" :class="statusType">
        {{ statusMsg }}
      </div>

      <div v-if="!loading && materials.length === 0" class="empty-state">
        <el-empty description="暂无数据" :image-size="80" />
      </div>

      <div v-else class="material-list">
        <div
          class="material-card"
          v-for="(m, index) in materials"
          :key="index"
        >
          <div class="material-header" @click="toggleCollapse(m)">
            <div class="material-index">{{ index + 1 }}</div>
            <div class="material-main">
              <div class="material-no">{{ m.materialNo || "-" }}</div>
              <div class="material-subrow">
                <div class="material-desc">{{ m.materialDesc || "-" }}</div>
                <div class="material-badges">
                  <el-tag
                    size="small"
                    round
                    :class="isZeroUnComplete(m) ? 'qty-tag-zero' : 'qty-tag-pending'"
                  >
                    待备料数：{{ m.unCompleteNum ?? "-" }}
                  </el-tag>
                  <el-tag size="small" type="info" round>需求数：{{ m.prepareNum ?? "-" }}</el-tag>
                </div>
              </div>
            </div>
            <div class="collapse-arrow" :class="{ collapsed: m._collapsed }"></div>
          </div>

          <div
            class="stock-table"
            v-show="!m._collapsed"
            v-if="Array.isArray(m.stockList) && m.stockList.length > 0"
          >
            <div class="stock-head">
              <div class="stock-col">库位</div>
              <div class="stock-col">托盘码</div>
              <div class="stock-col">数量</div>
            </div>
            <div class="stock-row" v-for="(s, sIndex) in m.stockList" :key="sIndex">
              <div class="stock-cell">
                <div class="stock-main">{{ s.wareName || "-" }}</div>
              </div>
              <div class="stock-cell">
                <div class="stock-main">{{ s.palletCode || "-" }}</div>
              </div>
              <div class="stock-cell">
                <el-input
                  type="number"
                  v-model="s._prepareQty"
                  placeholder="请输入备料数量"
                  inputmode="decimal"
                  @blur="handleQtyBlur(m, s)"
                  size="small"
                />
                <div class="qty-hint">可用库存：{{ s.stockNum || "-" }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <footer class="fixed-footer">
      <el-button round class="footer-cancel" :disabled="submitting" @click="handleCancel">
        全部取消
      </el-button>
      <el-button type="primary" round class="footer-primary" :disabled="submitting" :loading="submitting" @click="showConfirmModal = true">
        {{ submitting ? "提交中..." : "全部备料完成" }}
      </el-button>
    </footer>

    <el-dialog v-model="showConfirmModal" title="确认提交" width="85%" class="confirm-dialog">
      <div class="confirm-msg">确认要提交所有备料吗？提交后将完成备料操作。</div>
      <template #footer>
        <el-button round @click="showConfirmModal = false">取消</el-button>
        <el-button type="primary" round class="light-btn" @click="handleSubmitAll" :loading="submitting">
          确认提交
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import toast from "@/utils/toast";
import { getPrepareDetail, submitPrepare } from "@/api/prepare";

export default {
  name: "PrepareDetail",
  setup() {
    const router = useRouter();
    const route = useRoute();

    const prepareId = String(route.params.id || "");

    const loading = ref(false);
    const statusMsg = ref("");
    const statusType = ref("info");
    const headerInfo = ref(null);
    const materials = ref([]);
    const submitting = ref(false);
    const showConfirmModal = ref(false);

    const goPrepareList = () => router.push("/prepare-list");
    const handleBack = () => goPrepareList();
    const handleCancel = () => goPrepareList();

    const loadHeaderInfo = () => {
      if (!prepareId) return;
      try {
        const raw = sessionStorage.getItem(`wms_prepare_header_${prepareId}`);
        if (!raw) return;
        headerInfo.value = JSON.parse(raw);
      } catch {
        headerInfo.value = null;
      }
    };

    const fetchDetail = async () => {
      if (!prepareId) {
        toast.error("缺少备料ID");
        return;
      }
      try {
        loading.value = true;
        statusMsg.value = "";
        const res = await getPrepareDetail(prepareId);
        const data = res?.data;

        const list = Array.isArray(data)
          ? data
          : data?.prepareMaterialDetailList ||
            data?.materialList ||
            data?.wareOutDetailList ||
            res?.prepareMaterialDetailList ||
            res?.materialList ||
            res?.wareOutDetailList ||
            [];

        const normalized = Array.isArray(list) ? list : [];
        normalized.forEach((m) => {
          const stockList = Array.isArray(m?.stockList) ? m.stockList : [];
          stockList.forEach((s) => {
            if (s && s._prepareQty === undefined) s._prepareQty = "";
          });
          autoFillPrepareQty(m);
          m._collapsed = isZeroUnComplete(m);
        });
        materials.value = normalized;
      } catch (e) {
        statusMsg.value = e?.message || "加载失败";
        statusType.value = "error";
      } finally {
        loading.value = false;
      }
    };

    const isZeroUnComplete = (m) => {
      const n = toNumberOrNaN(m?.unCompleteNum);
      return !Number.isNaN(n) && n <= 0;
    };

    const toggleCollapse = (m) => {
      if (!m) return;
      m._collapsed = !m._collapsed;
    };

    const toNumberOrNaN = (val) => {
      if (val === null || val === undefined || val === "") return NaN;
      const n = Number(val);
      return Number.isFinite(n) ? n : NaN;
    };

    const clamp = (num, min, max) => {
      if (!Number.isFinite(num)) return num;
      let n = num;
      if (Number.isFinite(min)) n = Math.max(min, n);
      if (Number.isFinite(max)) n = Math.min(max, n);
      return n;
    };

    const formatQty = (val) => {
      if (val === null || val === undefined || val === "") return "0";
      const n = Number(val);
      if (!Number.isFinite(n)) return "0";
      return String(n);
    };

    const resolveTargetQty = (m) => {
      const a = toNumberOrNaN(m?.unCompleteNum);
      if (!Number.isNaN(a)) return a;
      const b = toNumberOrNaN(m?.prepareNum);
      if (!Number.isNaN(b)) return b;
      return NaN;
    };

    const materialQtySum = (m, { excludeStock } = {}) => {
      const stockList = Array.isArray(m?.stockList) ? m.stockList : [];
      let sum = 0;
      for (let i = 0; i < stockList.length; i += 1) {
        const s = stockList[i];
        if (excludeStock && s === excludeStock) continue;
        const qty = toNumberOrNaN(s?._prepareQty);
        if (!Number.isNaN(qty) && qty > 0) sum += qty;
      }
      return sum;
    };

    const autoFillPrepareQty = (m) => {
      const stockList = Array.isArray(m?.stockList) ? m.stockList : [];
      const targetQty = resolveTargetQty(m);
      if (!Number.isFinite(targetQty) || targetQty <= 0) return;

      let remaining = targetQty;
      for (let i = 0; i < stockList.length; i += 1) {
        const s = stockList[i];
        const available = toNumberOrNaN(s?.stockNum);
        const canUse =
          Number.isFinite(available) && available >= 0 ? available : remaining;
        const next = clamp(remaining, 0, canUse);
        s._prepareQty = next > 0 ? String(next) : "";
        remaining -= next;
        if (remaining <= 0) break;
      }
    };

    const handleQtyBlur = (m, s) => {
      const targetQty = resolveTargetQty(m);
      if (!Number.isFinite(targetQty) || targetQty <= 0) return;

      const current = toNumberOrNaN(s?._prepareQty);
      if (Number.isNaN(current) || current <= 0) {
        s._prepareQty = "";
        return;
      }

      const available = toNumberOrNaN(s?.stockNum);
      const maxByStock =
        Number.isFinite(available) && available >= 0 ? available : Infinity;
      const others = materialQtySum(m, { excludeStock: s });
      const maxByTarget = targetQty - others;
      const next = clamp(current, 0, Math.min(maxByStock, maxByTarget));

      if (!Number.isFinite(next) || next <= 0) {
        s._prepareQty = "";
        return;
      }

      if (Math.abs(next - current) > 1e-9) {
        s._prepareQty = String(next);
        toast.error("数量不能超过待备料数量");
      }
    };

    const buildPayload = () => {
      const wareOutDetailList = [];

      for (let i = 0; i < materials.value.length; i += 1) {
        const m = materials.value[i];
        const stockList = Array.isArray(m?.stockList) ? m.stockList : [];
        const targetQty = resolveTargetQty(m);

        const selectedStocks = [];
        let sum = 0;

        for (let j = 0; j < stockList.length; j += 1) {
          const s = stockList[j];
          const qty = toNumberOrNaN(s?._prepareQty);
          if (Number.isNaN(qty) || qty <= 0) continue;

          if (Number.isFinite(targetQty) && sum + qty - targetQty > 1e-6) {
            throw new Error(
              `物料 ${
                m?.materialNo || ""
              } 数量不能超过待备料数（${targetQty}）`,
            );
          }

          // const available = toNumberOrNaN(s?.stockNum)
          // if (!Number.isNaN(available) && qty > available) {
          //   throw new Error(`物料 ${m?.materialNo || ''} 库位 ${s?.palletCode || ''} 备料数量不能大于可用库存`)
          // }

          if (!s?.palletId || !s?.wareId || !s?.batchNo) {
            throw new Error(
              `物料 ${m?.materialNo || ""} 库位数据不完整，无法提交`,
            );
          }

          selectedStocks.push({
            palletId: s?.palletId,
            wareId: s?.wareId,
            batchNo: s?.batchNo,
            wareOutNum: formatQty(qty),
            boxList: [],
          });
          sum += qty;
        }

        if (selectedStocks.length === 0) continue;

        // if (!Number.isNaN(targetQty)) {
        //   const diff = Math.abs(sum - targetQty)
        //   if (diff > 1e-6) {
        //     throw new Error(`物料 ${m?.materialNo || ''} 备料数量合计必须等于待备料数（${targetQty}）`)
        //   }
        // }

        wareOutDetailList.push({
          materialId: m?.materialId,
          wareOutNum: sum,
          stockList: selectedStocks,
        });
      }

      return {
        id: prepareId,
        wareOutDetailList,
      };
    };

    const handleSubmitAll = async () => {
      if (submitting.value) return;
      try {
        const payload = buildPayload();
        if (
          !Array.isArray(payload.wareOutDetailList) ||
          payload.wareOutDetailList.length === 0
        ) {
          toast.error("请先输入备料数量");
          return;
        }

        submitting.value = true;
        await submitPrepare(payload);
        toast.success("备料完成");
        goPrepareList();
      } catch (e) {
        toast.error(e?.message || "提交失败");
      } finally {
        submitting.value = false;
      }
    };

    onMounted(() => {
      loadHeaderInfo();
      fetchDetail();
    });

    return {
      loading,
      statusMsg,
      statusType,
      headerInfo,
      materials,
      submitting,
      showConfirmModal,
      handleBack,
      handleCancel,
      handleSubmitAll,
      handleQtyBlur,
      toggleCollapse,
      isZeroUnComplete,
    };
  },
};
</script>

<style scoped>
.page-container {
  min-height: 100vh;
  background: var(--background-color);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  padding-bottom: calc(80px + env(safe-area-inset-bottom));
}

.page-container,
.page-container * {
  box-sizing: border-box;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  padding-top: calc(10px + env(safe-area-inset-top));
  background-color: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px var(--shadow-light);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: calc(52px + env(safe-area-inset-top));
}

.back-btn {
  width: 36px;
  height: 36px;
}

.icon-back {
  width: 20px;
  height: 20px;
}

.title {
  font-size: 16px;
  font-weight: 600;
  color: var(--header-text);
  margin: 0;
  flex: 1;
  text-align: center;
}

.header-right {
  width: 36px;
}

.content {
  padding: 8px 10px;
  margin-top: calc(52px + env(safe-area-inset-top));
}

.header-info {
  background: var(--card-background);
  border-radius: 8px;
  padding: 8px 10px;
  margin-bottom: 8px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 3px 0;
  font-size: 12px;
}

.info-label {
  color: var(--text-secondary);
}

.info-value {
  color: var(--text-primary);
  text-align: right;
}

.status-banner {
  padding: 8px 10px;
  border-radius: 8px;
  margin-bottom: 8px;
  font-size: 12px;
}

.status-banner.info {
  background-color: rgba(35, 177, 192, 0.12);
  color: var(--primary-color);
}

.status-banner.error {
  background-color: rgba(239, 68, 68, 0.12);
  color: #ef4444;
}

.empty-state {
  text-align: center;
  padding: 40px 0;
}

.material-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.material-card {
  background: var(--card-background);
  border-radius: 8px;
  padding: 8px;
}

.material-header {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  cursor: pointer;
}

.collapse-arrow {
  flex: 0 0 12px;
  width: 12px;
  height: 12px;
  margin-top: 5px;
  border-right: 2px solid var(--text-secondary);
  border-bottom: 2px solid var(--text-secondary);
  transform: rotate(45deg);
  transition: transform 0.2s ease;
}

.collapse-arrow.collapsed {
  margin-top: 8px;
  transform: rotate(-45deg);
}

.material-index {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: var(--primary-color);
  color: var(--primary-text);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  flex-shrink: 0;
}

.material-main {
  flex: 1;
  min-width: 0;
}

.material-no {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
}

.material-subrow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin-top: 4px;
}

.material-desc {
  flex: 1;
  min-width: 0;
  color: var(--text-secondary);
  font-size: 11px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.material-badges {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.qty-tag-zero {
  --el-tag-bg-color: #f3f4f6;
  --el-tag-text-color: #9ca3af;
  --el-tag-border-color: #e5e7eb;
}

.qty-tag-pending {
  --el-tag-bg-color: #fef2f2;
  --el-tag-text-color: #ef4444;
  --el-tag-border-color: #fecaca;
}

.stock-table {
  margin-top: 6px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  overflow: hidden;
}

.stock-head,
.stock-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
}

.stock-head {
  background: var(--background-color);
  font-size: 11px;
  color: var(--text-secondary);
  font-weight: 600;
}

.stock-col {
  padding: 6px 8px;
}

.stock-cell {
  padding: 6px 8px;
  border-top: 1px solid var(--border-color);
}

.stock-cell :deep(.el-input__wrapper) {
  border-radius: 6px;
}

.stock-main {
  color: var(--text-primary);
  font-size: 12px;
}

.qty-hint {
  margin-top: 3px;
  color: var(--text-secondary);
  font-size: 11px;
}

.fixed-footer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  background: var(--card-background);
  padding: 8px 10px;
  padding-bottom: calc(8px + env(safe-area-inset-bottom));
  display: flex;
  gap: 8px;
  border-top: 1px solid var(--border-color);
  box-shadow: 0 -2px 8px var(--shadow-light);
}

.footer-cancel {
  flex: 1;
  height: 40px;
}

.footer-primary {
  flex: 1;
  height: 40px;
  --el-button-bg-color: #5bc0de !important;
  --el-button-border-color: #5bc0de !important;
}

.light-btn.el-button--primary {
  --el-button-bg-color: #5bc0de !important;
  --el-button-border-color: #5bc0de !important;
}

.confirm-dialog :deep(.el-dialog) {
  border-radius: 12px;
  max-width: 300px;
  margin: 0 auto;
}

.confirm-dialog :deep(.el-dialog__header) {
  padding: 12px 16px;
  margin: 0;
  border-bottom: 1px solid var(--border-color);
  text-align: center;
}

.confirm-dialog :deep(.el-dialog__title) {
  font-size: 15px;
  font-weight: 600;
}

.confirm-dialog :deep(.el-dialog__body) {
  padding: 16px;
}

.confirm-dialog :deep(.el-dialog__footer) {
  padding: 10px 16px 16px;
  display: flex;
  gap: 8px;
}

.confirm-dialog :deep(.el-dialog__footer .el-button) {
  flex: 1;
}

.confirm-msg {
  font-size: 14px;
  color: var(--text-primary);
  text-align: center;
  line-height: 1.5;
}
</style>
